import { create } from 'zustand';
import produce from 'immer';

import { fetchScheduledCallDetails } from './apiStore';

import {
  deepSet,
  flattenObject,
  validationMsg,
} from '../utils/activityTrackerHelperFn';

import { ActivityType, ParamType } from '../types/activityTrackerStore';
import { formattedPhoneNumber, getComments } from 'src/utils/common';
import { fetchCaller, fetchCountryDetails } from 'src/store/apiStore';

export const useActivityStore = create<ActivityType>((set, get) => ({
  isDetailsLoading: false,
  callDetails: {},
  isSaveClicked: false,
  countryInfo: [],
  personInfo: {},
  hospitalFormDetails: {},
  personRepoValues: {},
  conflict: {},
  originalCallDetails: {},

  //not uisng
  getScheduledCallDetails: async (params: Partial<ParamType>) => {
    const { token, tokenType, submissionId } = params;
    const countryCode = await fetchCountryDetails({ token, tokenType });

    set(
      produce(state => {
        state.isDetailsLoading = true;
        state.countryInfo = countryCode?.Data;
      }),
    );

    if (submissionId) {
      const scheduledCallDetails = await fetchScheduledCallDetails({
        token,
        tokenType,
        submissionId,
      });

      const primaryPhone = scheduledCallDetails?.PersonOfConcern?.PrimaryPhone;

      set(
        produce(state => {
          state.callDetails = { ...scheduledCallDetails };
          state.callDetails.leaveMessage =
            primaryPhone?.OkToLeaveMessage === true
              ? 1
              : primaryPhone?.OkToLeaveMessage === false
              ? 2
              : -1;
          state.callDetails.phoneType = primaryPhone?.PhoneTypeId;
          state.callDetails.phoneNumber =
            primaryPhone?.CountryId === null
              ? formattedPhoneNumber(primaryPhone?.Number, 1)
              : primaryPhone?.Number ?? '';

          state.callDetails.countryId = primaryPhone?.CountryId ?? 1;
          state.callDetails.comments = getComments(
            primaryPhone,
            primaryPhone?.CountryId,
            get().countryInfo,
          );
          if (
            primaryPhone?.CountryId === null &&
            primaryPhone.Number !== null
          ) {
            state.callDetails.comments =
              primaryPhone?.Comments !== null
                ? primaryPhone?.Comments + primaryPhone?.Number
                : primaryPhone?.Number;
          } else {
            state.callDetails.comments = primaryPhone?.Comments ?? '';
          }
          state.isDetailsLoading = false;
        }),
      );
    }
  },

  updateScheduledCallDetails: (key: string, value: any) => {
    set(
      produce(state => {
        state.callDetails = {
          ...state.callDetails,
          [key]: value,
        };
      }),
    );
  },

  showValidationMsg: (type: string): boolean => {
    switch (type) {
      case 'phnNumber':
        return validationMsg(get().callDetails, 'phoneNumber');
      case 'scheduledTime':
        return validationMsg(get().callDetails, 'scheduledTime');
      case 'timezone':
        return validationMsg(get().callDetails, 'timezone');
      case 'caseId':
        return validationMsg(get().callDetails, 'caseId');
      default:
        return false;
    }
  },

  setSaveClicked: (value: boolean) => {
    set(
      produce(state => {
        state.isSaveClicked = value;
      }),
    );
  },

  deleteScheduleCallDetails: () => {
    set((state: any) => {
      return {
        ...state,
        callDetails: undefined,
        isDetailsLoading: false,
        isSaveClicked: false,
        conflict: {},
        personRepoValues: {},
      };
    });
  },

  setPersonDetailsFromHF: async (params: any) => {
    const { data, token, tokenType } = params;
    const countryCode = await fetchCountryDetails({ token, tokenType });

    set(
      produce(state => {
        state.countryInfo = countryCode?.Data;
      }),
    );

    set(
      produce(state => {
        const primaryPhone = data?.PersonOfConcern?.PrimaryPhone;
        state.callDetails = {
          ...data.PersonOfConcern,
          AccountNameAndNumber: data.Account.AccountNameAndNumber,
          AccountId: data.AccountId,
          fromHF: true,
        };

        state.hospitalFormDetails = { ...data.PersonOfConcern };
        state.callDetails.leaveMessage =
          primaryPhone?.OkToLeaveMessage === true
            ? 1
            : primaryPhone?.OkToLeaveMessage === false
            ? 2
            : -1;
        state.callDetails.phoneType = primaryPhone?.PhoneTypeId;
        state.callDetails.phoneNumber =
          primaryPhone?.CountryId === null
            ? formattedPhoneNumber(primaryPhone?.Number, 1)
            : primaryPhone?.Number ?? '';

        state.callDetails.countryId = primaryPhone?.CountryId ?? 1;
        state.callDetails.comments = getComments(
          primaryPhone,
          primaryPhone?.CountryId,
          get().countryInfo,
        );
        if (primaryPhone?.CountryId === null && primaryPhone.Number !== null) {
          state.callDetails.comments =
            primaryPhone?.Comments !== null
              ? primaryPhone?.Comments + primaryPhone?.Number
              : primaryPhone?.Number;
        } else {
          state.callDetails.comments = primaryPhone?.Comments ?? '';
        }
      }),
    );

    set(
      produce(state => {
        state.originalCallDetails = { ...get().callDetails };
      }),
    );
  },

  updatePersonInfo: async (
    accountId: number | undefined,
    callerDataId: number,
    token: string,
    tokenType: string,
  ) => {
    if (!accountId || !callerDataId) return;

    const callerDetails = await fetchCaller({
      token,
      tokenType,
      accountId,
      personId: callerDataId,
    });

    const hfDetails = get().hospitalFormDetails ?? {};
    const hfPhone = hfDetails?.PrimaryPhone ?? {};
    const repoPhone = callerDetails?.PrimaryPhone ?? {};
    const countryInfo = get().countryInfo;

    const isEmpty = (val: any) =>
      val === null || val === undefined || val === '';

    const mergedCountryId = isEmpty(hfPhone?.CountryId)
      ? repoPhone?.CountryId
      : hfPhone.CountryId;

    const matchedCountry = countryInfo?.find(c => c.Id === mergedCountryId);

    const mergedPhone = {
      Number:
        isEmpty(hfPhone?.Number) &&
        (repoPhone?.CountryId === null || repoPhone?.CountryId === undefined)
          ? formattedPhoneNumber(repoPhone?.Number, 1)
          : isEmpty(hfPhone?.Number)
          ? repoPhone?.Number ?? ''
          : hfPhone?.Number,
      CountryId: mergedCountryId,
      Country: matchedCountry ?? null,
      PhoneTypeId: isEmpty(hfPhone?.PhoneTypeId)
        ? repoPhone?.PhoneTypeId
        : hfPhone.PhoneTypeId,
      PhoneType: isEmpty(hfPhone?.PhoneType)
        ? repoPhone?.PhoneType
        : hfPhone.PhoneType,
      OkToLeaveMessage: isEmpty(hfPhone?.OkToLeaveMessage)
        ? repoPhone?.OkToLeaveMessage
        : hfPhone.OkToLeaveMessage,
      Comments: isEmpty(hfPhone?.Comments)
        ? repoPhone?.Comments ?? ''
        : hfPhone.Comments,
    };

    const leaveMessage =
      mergedPhone?.OkToLeaveMessage === true
        ? 1
        : mergedPhone?.OkToLeaveMessage === false
        ? 2
        : -1;

    let comments = getComments(
      mergedPhone,
      mergedPhone?.CountryId,
      countryInfo,
    );

    if (mergedPhone?.CountryId === null && mergedPhone.Number !== null) {
      comments =
        mergedPhone?.Comments !== null
          ? mergedPhone?.Comments + mergedPhone?.Number
          : mergedPhone?.Number;
    } else {
      comments = mergedPhone?.Comments ?? '';
    }

    set(
      produce(state => {
        state.callDetails = {
          ...hfDetails,
          PrimaryPhone: mergedPhone,
          fromHF: true,
          leaveMessage,
          phoneType: mergedPhone?.PhoneTypeId,
          phoneNumber:
            mergedPhone?.CountryId === null
              ? formattedPhoneNumber(mergedPhone?.Number, 1)
              : mergedPhone?.Number ?? '',
          countryId: mergedCountryId,
          comments,
        };

        const primaryPhone = callerDetails?.PrimaryPhone;

        state.personRepoValues = {
          ...callerDetails,
          fromHF: false,
        };
        state.personRepoValues.leaveMessage =
          primaryPhone?.OkToLeaveMessage === true
            ? 1
            : primaryPhone?.OkToLeaveMessage === false
            ? 2
            : -1;
        state.personRepoValues.phoneType = primaryPhone?.PhoneTypeId;
        state.personRepoValues.phoneNumber =
          primaryPhone?.CountryId === null
            ? formattedPhoneNumber(primaryPhone?.Number, 1)
            : primaryPhone?.Number ?? '';
        state.personRepoValues.countryId = primaryPhone?.CountryId ?? 1;

        state.personRepoValues.comments = getComments(
          primaryPhone,
          primaryPhone?.CountryId,
          countryInfo,
        );

        if (primaryPhone?.CountryId === null && primaryPhone.Number !== null) {
          state.personRepoValues.comments =
            primaryPhone?.Comments !== null
              ? primaryPhone?.Comments + primaryPhone?.Number
              : primaryPhone?.Number;
        } else {
          state.personRepoValues.comments = primaryPhone?.Comments ?? '';
        }
      }),
    );

    get().setInitialFormWithConflictCheck();
  },

  setInitialFormWithConflictCheck: () => {
    const { callDetails: hospitalFormDetails, personRepoValues } = get();
    if (!hospitalFormDetails || !personRepoValues) return;
    const conflictMap: Record<
      string,
      { hfValue: any; repoValue: any; activeSource: 'hospital' | 'repo' }
    > = {};

    const flatHF = flattenObject(hospitalFormDetails);
    const flatRepo = flattenObject(personRepoValues);

    const updatedCallDetails = structuredClone(hospitalFormDetails);

    const allKeys = new Set([...Object.keys(flatHF), ...Object.keys(flatRepo)]);

    for (const key of Array.from(allKeys)) {
      const hfValue = flatHF[key];
      const repoValue = flatRepo[key];

      const normalize = (v: any) => (v === undefined ? null : v);
      const normHF = normalize(hfValue);
      const normRepo = normalize(repoValue);

      const valuesDiffer = JSON.stringify(normHF) !== JSON.stringify(normRepo);

      if (valuesDiffer) {
        conflictMap[key] = {
          hfValue: normHF,
          repoValue: normRepo,
          activeSource: 'hospital',
        };

        if (normHF === null && normRepo !== null && normRepo !== '') {
          deepSet(updatedCallDetails, key, normRepo);
        }
      }
    }

    set(
      produce(state => {
        state.conflict = conflictMap;
        state.callDetails = updatedCallDetails;
      }),
    );
  },

  toggleFieldSource: (key: string, list?: any) => {
    const conflict = get().conflict?.[key];
    if (!conflict) return;

    const newSource =
      conflict.activeSource === 'hospital' ? 'repo' : 'hospital';
    const newValue =
      newSource === 'hospital' ? conflict.hfValue : conflict.repoValue;

    set(
      produce(state => {
        deepSet(state.callDetails, key, newValue);
        state.conflict[key].activeSource = newSource;

        if (key === 'PrimaryPhone.PhoneType.Description') {
          const matchedPhoneType = list?.find(
            (type: any) => type.label === newValue,
          );
          const fallback = {
            label: newValue ?? '',
            value: -1,
          };
          const finalPhoneType = matchedPhoneType ?? fallback;
          state.callDetails.PrimaryPhone.PhoneType = {
            Description: finalPhoneType.label,
            Id: finalPhoneType.value,
          };
          state.callDetails.phoneType = state.callDetails.phoneType =
            finalPhoneType.value !== undefined
              ? finalPhoneType.value.toString()
              : '-1';
        }
        if (key === 'PrimaryPhone.OkToLeaveMessage') {
          const okToLeave = newValue ?? null;
          state.callDetails.PrimaryPhone.OkToLeaveMessage = okToLeave;
          state.callDetails.leaveMessage =
            okToLeave === true ? 1 : okToLeave === false ? 2 : -1;
        }

        if (key === 'PrimaryPhone.Comments') {
          const comments = newValue ?? '';
          state.callDetails.PrimaryPhone.Comments = comments;
          state.callDetails.comments = comments;
        }

        if (key === 'PrimaryPhone.Number') {
          const number = newValue ?? '';
          const conflict = get().conflict;

          const countryId =
            newSource === 'hospital'
              ? conflict?.countryId?.hfValue
              : conflict?.countryId?.repoValue;

          const matchedCountry = list?.find(
            (country: any) => country.Id === countryId,
          );

          state.callDetails.PrimaryPhone.Number = number;
          state.callDetails.phoneNumber = number;

          if (matchedCountry) {
            state.callDetails.PrimaryPhone = {
              ...state.callDetails.PrimaryPhone,
              Country: { ...matchedCountry },
              CountryId: matchedCountry.Id,
            };
            state.callDetails.countryId = matchedCountry.Id;
          }
        }
      }),
    );
  },

  setRemovePerson: () => {
    set((state: any) => {
      return {
        ...state,
        callDetails: { ...get().originalCallDetails },
        conflict: {},
        personRepoValues: {},
      };
    });
  },
}));
